import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { useAuth } from '../App';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { ScrollArea } from '../components/ui/scroll-area';
import ReactMarkdown from 'react-markdown';
import AppSidebar from '../components/AppSidebar';
import {
  Home,
  FolderOpen,
  Search,
  Library,
  Settings,
  BarChart3,
  LogOut,
  Download,
  Loader2,
  AlertCircle,
  FileText,
  FileSpreadsheet,
  FileType,
  ArrowLeft,
  Sparkles,
  Copy,
  Tag,
  Hash,
  Clock,
  ChevronLeft,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const fileIcon = (filename) => {
  const ext = (filename?.split('.').pop() || '').toLowerCase();
  if (['xlsx', 'xls'].includes(ext)) return FileSpreadsheet;
  if (['txt', 'md', 'html', 'htm'].includes(ext)) return FileType;
  return FileText;
};

const isPdf = (filename) => (filename || '').toLowerCase().endsWith('.pdf');
const formatBytes = (b) => {
  if (!b && b !== 0) return '—';
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / 1024 / 1024).toFixed(1)} MB`;
};


const mdComponents = {
  h1: ({ children }) => <h1 className="text-xl font-bold text-[#0F172A] mt-3 mb-2">{children}</h1>,
  h2: ({ children }) => <h2 className="text-base font-bold text-[#1E3A8A] mt-4 mb-1.5 border-b border-slate-200 pb-1">{children}</h2>,
  h3: ({ children }) => <h3 className="text-sm font-semibold text-slate-800 mt-3 mb-1">{children}</h3>,
  p: ({ children }) => <p className="text-sm text-slate-700 leading-relaxed mb-2">{children}</p>,
  strong: ({ children }) => <strong className="font-semibold text-slate-900">{children}</strong>,
  ul: ({ children }) => <ul className="list-disc pl-5 mb-2 space-y-0.5">{children}</ul>,
  ol: ({ children }) => <ol className="list-decimal pl-5 mb-2 space-y-0.5">{children}</ol>,
  li: ({ children }) => <li className="text-sm text-slate-700">{children}</li>,
};

// Highlight a needle inside a string (case-insensitive). Returns React nodes.
const highlight = (text, needle) => {
  if (!needle) return text;
  try {
    const escaped = needle.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const parts = text.split(new RegExp(`(${escaped})`, 'gi'));
    return parts.map((p, i) =>
      p.toLowerCase() === needle.toLowerCase()
        ? <mark key={i} className="bg-amber-200 text-amber-900 rounded px-0.5">{p}</mark>
        : <React.Fragment key={i}>{p}</React.Fragment>
    );
  } catch (err) {
    // Regex compile failed (extremely unlikely after escaping). Render raw text.
    console.warn('highlight() regex failed:', err);
    return text;
  }
};

export default function LibraryDocumentView() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { libDocId } = useParams();

  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pdfBlobUrl, setPdfBlobUrl] = useState(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [chunks, setChunks] = useState([]);
  const [chunksLoading, setChunksLoading] = useState(false);
  const [chunkTotal, setChunkTotal] = useState(0);

  // Right panel
  const [rightTab, setRightTab] = useState('search'); // 'search' | 'summary' | 'meta'
  const [searchQuery, setSearchQuery] = useState('');
  const [searchHits, setSearchHits] = useState(null);
  const [searching, setSearching] = useState(false);
  const [summary, setSummary] = useState(null);
  const [summarizing, setSummarizing] = useState(false);

  const pdfBlobUrlRef = useRef(null);

  // Fetch metadata
  const fetchDoc = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/library/${libDocId}`);
      setDoc(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load document');
      setDoc(null);
    } finally {
      setLoading(false);
    }
  }, [libDocId]);

  // Fetch chunks (for non-PDFs or text view)
  const fetchChunks = useCallback(async () => {
    setChunksLoading(true);
    try {
      const res = await axios.get(`${API}/library/${libDocId}/chunks`, { params: { limit: 500 } });
      setChunks(res.data.items || []);
      setChunkTotal(res.data.chunk_count_total || 0);
    } catch (err) {
      console.warn('Chunk fetch failed', err);
    } finally {
      setChunksLoading(false);
    }
  }, [libDocId]);

  // Fetch PDF as blob (since iframes can't pass Authorization header)
  const fetchPdf = useCallback(async () => {
    setPdfLoading(true);
    try {
      const res = await axios.get(
        `${API}/library/${libDocId}/download`,
        { params: { inline: true }, responseType: 'blob' },
      );
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      if (pdfBlobUrlRef.current) window.URL.revokeObjectURL(pdfBlobUrlRef.current);
      pdfBlobUrlRef.current = url;
      setPdfBlobUrl(url);
    } catch (err) {
      toast.error('Failed to load PDF preview');
    } finally {
      setPdfLoading(false);
    }
  }, [libDocId]);

  useEffect(() => { fetchDoc(); }, [fetchDoc]);

  // Once doc is loaded, fetch chunks always, and PDF if it's a PDF
  useEffect(() => {
    if (!doc) return;
    fetchChunks();
    if (isPdf(doc.filename)) fetchPdf();
  }, [doc, fetchChunks, fetchPdf]);

  // Cleanup blob URL on unmount
  useEffect(() => {
    return () => {
      if (pdfBlobUrlRef.current) window.URL.revokeObjectURL(pdfBlobUrlRef.current);
    };
  }, []);

  const handleDownload = async () => {
    if (!doc) return;
    try {
      const res = await axios.get(`${API}/library/${doc.lib_doc_id}/download`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = window.document.createElement('a');
      a.href = url;
      a.download = doc.filename;
      a.style.display = 'none';
      window.document.body.appendChild(a);
      a.click();
      window.document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Library doc download failed:', err);
      toast.error('Download failed');
    }
  };

  const handleSearchWithin = async () => {
    if (!searchQuery.trim() || searching) return;
    setSearching(true);
    setSearchHits(null);
    try {
      const res = await axios.post(`${API}/library/search`, {
        query: searchQuery.trim(),
        top_k: 8,
        lib_doc_ids: [libDocId],
      });
      setSearchHits(res.data.results || []);
      if ((res.data.results || []).length === 0) {
        toast.info('No matching passages in this document');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Search failed');
    } finally {
      setSearching(false);
    }
  };

  const handleSummarize = async () => {
    if (summarizing) return;
    setSummarizing(true);
    try {
      const res = await axios.post(`${API}/library/${libDocId}/summarize`, {}, { timeout: 60000 });
      setSummary(res.data.summary);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Summarization failed');
    } finally {
      setSummarizing(false);
    }
  };

  const handleUseInResearch = () => {
    // Pass the lib_doc_id via sessionStorage so /research can auto-select it
    try {
      const existing = JSON.parse(sessionStorage.getItem('research_lib_context') || '[]');
      if (!existing.find((d) => d.lib_doc_id === libDocId)) {
        existing.push({ lib_doc_id: libDocId, filename: doc.filename });
        sessionStorage.setItem('research_lib_context', JSON.stringify(existing));
      }
      toast.success('Added to Research context');
      navigate('/research');
    } catch (err) {
      // sessionStorage unavailable (private browsing, quota exceeded) — still navigate.
      console.warn('Could not stash research_lib_context in sessionStorage:', err);
      navigate('/research');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50/50">
        <AppSidebar user={user} onLogout={() => { logout(); navigate('/'); }} active="library" />
        <main className="ml-72 min-h-screen flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
        </main>
      </div>
    );
  }

  if (!doc) {
    return (
      <div className="min-h-screen bg-slate-50/50">
        <AppSidebar user={user} onLogout={() => { logout(); navigate('/'); }} active="library" />
        <main className="ml-72 min-h-screen flex items-center justify-center p-8">
          <Card className="max-w-md text-center">
            <CardContent className="p-8">
              <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
              <h2 className="text-lg font-bold text-slate-800 mb-2">Document not found</h2>
              <p className="text-sm text-slate-500 mb-4">It may have been deleted or you may not have access.</p>
              <Button onClick={() => navigate('/library')}><ArrowLeft className="w-4 h-4 mr-1.5" />Back to Library</Button>
            </CardContent>
          </Card>
        </main>
      </div>
    );
  }

  const Icon = fileIcon(doc.filename);
  const documentIsPdf = isPdf(doc.filename);

  return (
    <div className="min-h-screen bg-slate-50/50">
      <AppSidebar user={user} onLogout={() => { logout(); navigate('/'); }} active="library" />
      <main className="ml-72 min-h-screen flex flex-col">
        {/* Header */}
        <div className="bg-white border-b border-slate-200 px-6 py-4 sticky top-0 z-10">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-3 min-w-0">
              <Link to="/library" className="text-slate-400 hover:text-[#1E3A8A]" data-testid="back-to-library">
                <ChevronLeft className="w-5 h-5" />
              </Link>
              <div className="w-10 h-10 bg-[#1E3A8A]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                <Icon className="w-5 h-5 text-[#1E3A8A]" />
              </div>
              <div className="min-w-0">
                <h1 className="text-base font-bold text-[#0F172A] truncate" title={doc.filename} data-testid="doc-filename">{doc.filename}</h1>
                <div className="flex items-center gap-3 text-xs text-slate-500 mt-0.5">
                  <span><Hash className="w-3 h-3 inline mr-0.5" />{chunkTotal || doc.chunk_count} chunks</span>
                  <span>{formatBytes(doc.size_bytes)}</span>
                  {doc.page_count > 0 && <span>{doc.page_count} pages</span>}
                  <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${doc.status === 'ready' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>{doc.status}</span>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <Button size="sm" variant="outline" onClick={handleUseInResearch} data-testid="use-in-research-btn">
                <Sparkles className="w-3.5 h-3.5 mr-1.5" />Use in Research
              </Button>
              <Button size="sm" variant="outline" onClick={handleDownload} data-testid="download-original-btn">
                <Download className="w-3.5 h-3.5 mr-1.5" />Download Original
              </Button>
            </div>
          </div>
          {doc.tags?.length > 0 && (
            <div className="flex items-center gap-1.5 mt-2 ml-8 flex-wrap">
              <Tag className="w-3 h-3 text-slate-400" />
              {doc.tags.map((t, i) => (
                <span key={i} className="text-[11px] bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded">{t}</span>
              ))}
            </div>
          )}
        </div>

        {/* Body: split 2-pane */}
        <div className="flex-1 grid grid-cols-12 gap-4 p-4 min-h-0">
          {/* Left: viewer */}
          <div className="col-span-12 lg:col-span-8 min-h-0 flex flex-col">
            {documentIsPdf ? (
              pdfLoading ? (
                <div className="flex-1 flex items-center justify-center bg-white border border-slate-200 rounded-xl">
                  <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
                </div>
              ) : pdfBlobUrl ? (
                <iframe
                  src={pdfBlobUrl}
                  title={doc.filename}
                  className="flex-1 w-full bg-white border border-slate-200 rounded-xl"
                  style={{ minHeight: '70vh' }}
                  data-testid="pdf-iframe"
                />
              ) : (
                <div className="flex-1 flex items-center justify-center bg-white border border-slate-200 rounded-xl text-slate-500">
                  Unable to preview PDF.
                </div>
              )
            ) : (
              <Card className="flex-1 border-slate-200">
                <CardContent className="p-5 h-full flex flex-col">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-sm font-semibold text-slate-800 flex items-center gap-2">
                      <FileText className="w-4 h-4 text-[#1E3A8A]" />Extracted Text
                    </h3>
                    <span className="text-xs text-slate-400">
                      {chunkTotal} chunk{chunkTotal !== 1 ? 's' : ''}
                    </span>
                  </div>
                  {chunksLoading ? (
                    <div className="flex-1 flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
                  ) : chunks.length === 0 ? (
                    <p className="text-sm text-slate-500 text-center py-10">No extracted text available.</p>
                  ) : (
                    <ScrollArea className="flex-1 pr-3" data-testid="extracted-text-scroll">
                      <div className="space-y-3 text-sm text-slate-700 leading-relaxed">
                        {chunks.map((c) => (
                          <div key={c.chunk_id} id={`chunk-${c.chunk_index}`} className="border-l-2 border-slate-200 pl-3 py-1">
                            <p className="text-[10px] text-slate-400 mb-1">Chunk {c.chunk_index}</p>
                            <p className="whitespace-pre-wrap">{c.text}</p>
                          </div>
                        ))}
                      </div>
                    </ScrollArea>
                  )}
                </CardContent>
              </Card>
            )}
          </div>

          {/* Right: tools panel */}
          <div className="col-span-12 lg:col-span-4 min-h-0 flex flex-col">
            <Card className="flex-1 border-slate-200">
              <CardContent className="p-0 h-full flex flex-col">
                {/* Tabs */}
                <div className="flex border-b border-slate-200">
                  {[
                    { id: 'search', label: 'Search', Icon: Search },
                    { id: 'summary', label: 'AI Summary', Icon: Sparkles },
                    { id: 'meta', label: 'Metadata', Icon: Tag },
                  ].map((t) => (
                    <button
                      key={t.id}
                      onClick={() => setRightTab(t.id)}
                      className={`flex-1 px-3 py-2.5 text-xs font-medium border-b-2 flex items-center justify-center gap-1.5 ${
                        rightTab === t.id
                          ? 'border-[#1E3A8A] text-[#1E3A8A] bg-[#1E3A8A]/5'
                          : 'border-transparent text-slate-500 hover:text-slate-700 hover:bg-slate-50'
                      }`}
                      data-testid={`tab-${t.id}`}
                    >
                      <t.Icon className="w-3.5 h-3.5" />{t.label}
                    </button>
                  ))}
                </div>

                <div className="flex-1 overflow-hidden flex flex-col">
                  {rightTab === 'search' && (
                    <div className="flex-1 overflow-hidden flex flex-col p-4">
                      <p className="text-xs text-slate-500 mb-2">Semantic search within this document only.</p>
                      <div className="flex items-center gap-2 mb-3">
                        <div className="relative flex-1">
                          <Search className="w-3.5 h-3.5 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
                          <input
                            type="text"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            onKeyDown={(e) => { if (e.key === 'Enter') handleSearchWithin(); }}
                            placeholder="Search this document..."
                            className="w-full pl-8 pr-3 h-9 rounded-lg border border-slate-200 text-sm focus:outline-none focus:border-[#1E3A8A] bg-slate-50/50"
                            data-testid="search-within-input"
                          />
                        </div>
                        <Button
                          size="sm"
                          onClick={handleSearchWithin}
                          disabled={!searchQuery.trim() || searching}
                          className="bg-[#1E3A8A] hover:bg-[#1E3A8A]/90 text-white h-9"
                          data-testid="search-within-btn"
                        >
                          {searching ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Find'}
                        </Button>
                      </div>
                      <ScrollArea className="flex-1 pr-2">
                        {searchHits === null ? (
                          <p className="text-xs text-slate-400 text-center py-6">Run a search to find passages in this document.</p>
                        ) : searchHits.length === 0 ? (
                          <p className="text-xs text-slate-400 text-center py-6">No matching passages.</p>
                        ) : (
                          <div className="space-y-2">
                            {searchHits.map((hit, i) => (
                              <button
                                key={hit.chunk_id}
                                onClick={() => {
                                  // Scroll to chunk if it's rendered (non-PDF view)
                                  if (!documentIsPdf) {
                                    const el = window.document.getElementById(`chunk-${hit.chunk_index}`);
                                    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                                  }
                                }}
                                className="w-full text-left bg-slate-50 hover:bg-slate-100 rounded-lg p-2.5 transition-colors"
                                data-testid={`search-hit-${i}`}
                              >
                                <div className="flex items-center justify-between mb-1">
                                  <span className="text-[10px] font-mono text-slate-500">chunk {hit.chunk_index}</span>
                                  <span className="text-[10px] bg-emerald-50 text-emerald-700 px-1 py-0.5 rounded">RRF {hit.rrf_score?.toFixed(4)}</span>
                                </div>
                                <p className="text-xs text-slate-700 leading-relaxed line-clamp-4">
                                  {highlight(hit.text, searchQuery.trim())}
                                </p>
                              </button>
                            ))}
                          </div>
                        )}
                      </ScrollArea>
                    </div>
                  )}

                  {rightTab === 'summary' && (
                    <div className="flex-1 overflow-hidden flex flex-col p-4">
                      {!summary && !summarizing ? (
                        <div className="flex-1 flex flex-col items-center justify-center text-center">
                          <Sparkles className="w-10 h-10 text-amber-400 mb-3" />
                          <h4 className="text-sm font-semibold text-slate-800 mb-1">Generate AI Summary</h4>
                          <p className="text-xs text-slate-500 mb-4 max-w-xs">A focused overview of this document — key provisions, practical impact, and caveats. Powered by GPT-4o-mini.</p>
                          <Button
                            onClick={handleSummarize}
                            disabled={doc.status !== 'ready'}
                            className="bg-gradient-to-r from-amber-500 to-amber-600 text-white"
                            data-testid="generate-summary-btn"
                          >
                            <Sparkles className="w-3.5 h-3.5 mr-1.5" />Generate Summary
                          </Button>
                        </div>
                      ) : summarizing ? (
                        <div className="flex-1 flex flex-col items-center justify-center">
                          <Loader2 className="w-8 h-8 animate-spin text-amber-500 mb-3" />
                          <p className="text-sm text-slate-600">Reading the document and summarizing…</p>
                        </div>
                      ) : (
                        <>
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-xs font-semibold text-slate-600 uppercase tracking-wide">AI Summary</span>
                            <div className="flex items-center gap-1">
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => { navigator.clipboard.writeText(summary); toast.success('Copied'); }}
                                className="h-7 px-2"
                                data-testid="copy-summary-btn"
                              >
                                <Copy className="w-3 h-3 mr-1" />Copy
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => { setSummary(null); }}
                                className="h-7 px-2 text-xs"
                              >
                                Regenerate
                              </Button>
                            </div>
                          </div>
                          <ScrollArea className="flex-1 pr-2" data-testid="summary-content">
                            <div className="bg-gradient-to-br from-amber-50/40 to-white border border-amber-100 rounded-lg p-3">
                              <ReactMarkdown components={mdComponents}>{summary}</ReactMarkdown>
                            </div>
                          </ScrollArea>
                        </>
                      )}
                    </div>
                  )}

                  {rightTab === 'meta' && (
                    <ScrollArea className="flex-1 p-4">
                      <dl className="space-y-3 text-sm">
                        <div>
                          <dt className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Filename</dt>
                          <dd className="text-slate-800 mt-1 break-all">{doc.filename}</dd>
                        </div>
                        <div>
                          <dt className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Document ID</dt>
                          <dd className="text-slate-800 mt-1 font-mono text-xs">{doc.lib_doc_id}</dd>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <dt className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Size</dt>
                            <dd className="text-slate-800 mt-1">{formatBytes(doc.size_bytes)}</dd>
                          </div>
                          <div>
                            <dt className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Pages</dt>
                            <dd className="text-slate-800 mt-1">{doc.page_count || '—'}</dd>
                          </div>
                          <div>
                            <dt className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Chunks</dt>
                            <dd className="text-slate-800 mt-1">{chunkTotal || doc.chunk_count}</dd>
                          </div>
                          <div>
                            <dt className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Status</dt>
                            <dd className="text-slate-800 mt-1">{doc.status}</dd>
                          </div>
                        </div>
                        <div>
                          <dt className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Tags</dt>
                          <dd className="mt-1 flex flex-wrap gap-1">
                            {(doc.tags || []).length > 0
                              ? doc.tags.map((t, i) => (
                                  <span key={i} className="text-[11px] bg-slate-100 text-slate-700 px-1.5 py-0.5 rounded">{t}</span>
                                ))
                              : <span className="text-xs text-slate-400">No tags</span>}
                          </dd>
                        </div>
                        <div>
                          <dt className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Uploaded By</dt>
                          <dd className="text-slate-800 mt-1 text-xs">{doc.uploaded_by_email || '—'}</dd>
                        </div>
                        <div>
                          <dt className="text-xs font-semibold text-slate-500 uppercase tracking-wide flex items-center gap-1">
                            <Clock className="w-3 h-3" />Uploaded
                          </dt>
                          <dd className="text-slate-800 mt-1 text-xs">{doc.created_at?.split('T')[0]}</dd>
                        </div>
                        {doc.ingest_seconds && (
                          <div>
                            <dt className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Ingest time</dt>
                            <dd className="text-slate-800 mt-1 text-xs">{doc.ingest_seconds}s</dd>
                          </div>
                        )}
                      </dl>
                    </ScrollArea>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
    </div>
  );
}
